import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Users, Heart, ShieldCheck, Smile, Calendar, UserPlus, Shuffle, Handshake, Aperture, GalleryHorizontalEnd, Check } from "lucide-react";
import { PathCard } from "../components/PathCard";
import { StepCard } from "../components/StepCard";
import { SessionTypeCard } from "../components/SessionTypeCard";
import { TestimonialCard } from "../components/TestimonialCard";
import { SectionHeading } from "../components/SectionHeading";
import { Logo } from "../components/Logo";
import { useSettings } from "../context/SettingsContext";
import { useSessionTypes } from "../hooks/useSessionTypes";
import { api } from "../lib/api";
import { mediaUrl } from "../lib/mediaUrl";
import { formatDate } from "../lib/dateUtils";
import { IMG_HERO, TESTIMONIALS } from "../lib/images";

const STEPS = [
  { icon: Calendar, title: "Choose a Session", description: "Find a session that fits your vibe, location and schedule." },
  { icon: UserPlus, title: "Create Your Stranger Profile", description: "Tell us about yourself, your preferences and the type of connection you're open to." },
  { icon: Shuffle, title: "We Match", description: "We pair you with another stranger based on preferences, compatibility and session requirements." },
  { icon: Handshake, title: "Meet & Connect", description: "Arrive alone. Your host introduces you to your Stranger." },
  { icon: Aperture, title: "Capture the Moment", description: "Your photographer guides the experience while capturing genuine moments." },
  { icon: GalleryHorizontalEnd, title: "Get Your Photos", description: "Relive the experience through your professional photo gallery." },
];

const FEATURES = [
  { icon: Users, title: "Meet", desc: "We introduce you to someone new." },
  { icon: Heart, title: "Connect", desc: "Real conversation. Real connection." },
  { icon: Camera, title: "Capture", desc: "Professional photos. Real moments." },
  { icon: ShieldCheck, title: "Safe", desc: "Verified photographers. Clear guidelines." },
  { icon: Smile, title: "Unforgettable", desc: "A story you'll never forget." },
];

const STRANGER_INCLUDES = [
  "Reserves your spot in the session",
  "Paid directly to Stranger Sessions to confirm your registration",
  "Fully separate from whatever your photographer charges for the session itself",
  "Confirmed the moment your payment is verified",
];

const PHOTOGRAPHER_INCLUDES = [
  "Charged once you have a paying registration for your session",
  "You keep 100% of your own session pricing",
  "Paid directly to Stranger Sessions, not deducted from your earnings",
  "Unlocks matching, messaging and gallery delivery tools",
];

export default function Home() {
  const { settings } = useSettings();
  const sessionTypes = useSessionTypes();
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/photographers/featured").then(({ data }) => setFeatured(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[820px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMG_HERO} alt="A photographer capturing a Stranger Session in a golden field" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-midnight-black via-midnight-black/75 to-midnight-black/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-midnight-black via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-16 w-full">
          <h1 className="text-clean-white font-extrabold uppercase tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl animate-fade-up" data-testid="hero-headline">
            Two strangers.
            <br />
            One photographer.
            <br />
            <span className="text-stranger-orange">Zero idea</span> what
            <br />
            happens next.
          </h1>
          <p className="text-clean-white/80 font-medium max-w-xl mt-6 text-base sm:text-lg">
            Stranger Sessions brings photographers and strangers together for professionally hosted photo experiences built around genuine human connection.
          </p>
          <div className="flex items-center gap-4 mt-5">
            <span className="w-12 h-1 bg-stranger-orange" />
            <p className="text-stranger-orange font-bold text-base sm:text-lg" data-testid="hero-supporting-statement">
              Come alone. We'll handle the introduction.
            </p>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2" data-testid="two-path-section">
        <PathCard
          testId="path-card-photographer"
          variant="dark"
          icon={Camera}
          eyebrow="I'm a Photographer"
          heading="Host a Stranger Session."
          sub="We handle the strangers."
          lines={["You handle the camera."]}
          ctaLabel="HOST A SESSION"
          to="/photographers/join"
          statLabel="$10 Platform Fee Per Booking"
          statDesc="You keep 100% of your session payment."
        />
        <PathCard
          testId="path-card-stranger"
          variant="light"
          icon={Users}
          eyebrow="I Want to Be a Stranger"
          heading="Find a session near me."
          sub="Come alone."
          lines={["Leave with a story."]}
          ctaLabel="FIND A SESSION"
          to="/find-a-session"
          statLabel="$10 Booking Fee Per Session"
          statDesc="Paid to Stranger Sessions to reserve your spot."
        />
      </section>

      <section className="bg-midnight-black py-14" data-testid="meet-connect-capture-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-5 gap-8">
          {FEATURES.map((f) => (
            <div key={f.title}>
              <f.icon className="w-6 h-6 text-stranger-orange mb-3" strokeWidth={1.75} />
              <h4 className="font-extrabold uppercase text-sm tracking-wide mb-1 text-clean-white">{f.title}</h4>
              <p className="text-xs text-clean-white/60 font-medium leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-warm-white py-24" id="how-it-works" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="The Process" title="How Stranger Sessions Work" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-14">
            {STEPS.map((s, i) => (
              <StepCard key={s.title} number={i + 1} icon={s.icon} title={s.title} description={s.description} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-true-black py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="Find Your Fit" title="What Kind of Stranger Session Are You Looking For?" dark />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sessionTypes.map((t) => (
              <SessionTypeCard key={t.type_key} type={t.type_key} image={t.image_url} title={t.title} description={t.description} />
            ))}
          </div>
        </div>
      </section>

      {featured.length > 0 && (
        <section className="bg-true-black py-24" data-testid="featured-photographers-section">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <SectionHeading eyebrow="Spotlight" title="Featured Photographers" dark />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.map((p) => (
                <div key={p.id} className="bg-clean-white rounded-2xl p-6 flex flex-col" data-testid={`featured-photographer-${p.id}`}>
                  <div className="flex items-center gap-4 mb-4">
                    {p.profile_photo_url ? (
                      <img src={mediaUrl(p.profile_photo_url)} alt={p.business_name} className="w-16 h-16 rounded-full object-cover border-2 border-stranger-orange shrink-0" />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-warm-white border-2 border-soft-gray flex items-center justify-center shrink-0">
                        <Camera className="w-6 h-6 text-midnight-black/30" />
                      </div>
                    )}
                    <div>
                      <p className="font-extrabold uppercase text-sm">{p.business_name}</p>
                      <p className="text-xs font-medium text-midnight-black/60">{p.location}</p>
                    </div>
                  </div>
                  {p.bio && <p className="text-sm font-medium text-midnight-black/70 mb-4 line-clamp-3">{p.bio}</p>}
                  {p.next_session ? (
                    <Link to={`/sessions/${p.next_session.id}`} data-testid={`featured-next-session-${p.id}`} className="mt-auto text-xs font-bold uppercase tracking-wide text-stranger-orange hover:underline">
                      Next Session: {p.next_session.title} — {formatDate(p.next_session.session_date)}
                    </Link>
                  ) : (
                    <Link to={`/photographers/${p.slug}`} data-testid={`featured-view-profile-${p.id}`} className="mt-auto text-xs font-bold uppercase tracking-wide text-stranger-orange hover:underline">
                      View Profile
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-warm-white py-24 relative overflow-hidden" data-testid="pricing-transparency-section">
        <Logo className="hidden lg:block h-[480px] absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.06] grayscale pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 sm:px-8 relative z-10">
          <SectionHeading eyebrow="No Surprises" title="Simple. Transparent. Always Up Front." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div data-testid="pricing-strangers">
              <p className="text-stranger-orange text-xs font-bold tracking-[0.2em] uppercase mb-2">For Strangers</p>
              <p className="font-extrabold uppercase text-2xl sm:text-3xl mb-6">$10 Booking Fee Per Session</p>
              <ul className="space-y-4">
                {STRANGER_INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-stranger-orange shrink-0 mt-0.5" />
                    <span className="font-medium text-midnight-black/75">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="pricing-photographers">
              <p className="text-stranger-orange text-xs font-bold tracking-[0.2em] uppercase mb-2">For Photographers</p>
              <p className="font-extrabold uppercase text-2xl sm:text-3xl mb-6">$10 Platform Fee Per Booking</p>
              <ul className="space-y-4">
                {PHOTOGRAPHER_INCLUDES.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-stranger-orange shrink-0 mt-0.5" />
                    <span className="font-medium text-midnight-black/75">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-midnight-black py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
            <div>
              <p className="text-stranger-orange text-xs font-bold tracking-[0.25em] uppercase mb-3">Stories</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-clean-white">Real People. Real Connection. Real Stories.</h2>
            </div>
            {settings.stories_page_enabled && (
              <Link to="/stories" data-testid="read-more-stories-link">
                <button className="bg-stranger-orange text-clean-white font-bold tracking-widest text-xs px-8 py-4 rounded-full hover:bg-orange-600 transition-colors">READ STORIES</button>
              </Link>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-6 overflow-x-auto pb-2">
            {TESTIMONIALS.map((t) => (
              <TestimonialCard key={t.name} {...t} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
